'use client';

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import styles from '../styles/About.module.css';
import Image from 'next/image';

gsap.registerPlugin(ScrollTrigger); // Register plugin outside component

const AnimatedSection = ({ text, imagePosition, imageSrc, centered = false, backgroundImage = null }) => {
  // Use a single main ref for context scoping
  const mainRef = useRef(null);
  // Keep state for image errors
  const [imgError, setImgError] = useState(false);
  const [bgImgError, setBgImgError] = useState(false);
  
  useEffect(() => {
    // Use gsap.context for scoping and automatic cleanup
    const ctx = gsap.context(() => {
      // Target elements within the context
      const sectionElement = mainRef.current;
      const textElement = sectionElement.querySelector('h2'); // Target h2 directly
      const leftImgContainer = sectionElement.querySelector(`.${styles.imageContainer}:first-child`); // More specific selector if needed
      const rightImgContainer = sectionElement.querySelector(`.${styles.imageContainer}:last-child`); // More specific selector if needed
      const bgImgElement = sectionElement.querySelector('img[alt="Background"]'); // Target background image by alt text or a specific class if added
      
      if (!textElement) return;
            
      // Split text for animation
      const split = new SplitType(textElement, {
        types: "words, chars",
      });
      
      // Create the timeline with ScrollTrigger for text scrub
      gsap.timeline({ // No need to assign to variable if not used elsewhere
        scrollTrigger: {
          trigger: textElement,
          start: "top 100%",
          end: "bottom 70%",
          scrub: 0.5,
        },
      })
      .set(split.chars, { // Chain the set directly
        duration: 0.3,
        color: "#fff",
        stagger: 0.1,
      }, 0.1);
      
      // Animate left image container if it exists and imagePosition is left
      if (leftImgContainer && imagePosition === 'left') {
        gsap.fromTo(leftImgContainer, 
          { x: -100, opacity: 0, rotation: -15 }, 
          { x: 0, opacity: 1, rotation: 0, duration: 1.2, ease: "power2.out", 
            scrollTrigger: { trigger: leftImgContainer, start: "top 75%" }
          }
        );
      }
      
      // Animate right image container if it exists and imagePosition is right
      if (rightImgContainer && imagePosition === 'right') {
        gsap.fromTo(rightImgContainer, 
          { x: 100, opacity: 0, rotation: 15 }, 
          { x: 0, opacity: 1, rotation: 0, duration: 1.2, ease: "power2.out", 
            scrollTrigger: { trigger: rightImgContainer, start: "top 75%" }
          }
        );
      }
      
      // Add parallax effect for background image section
      if (backgroundImage && sectionElement && bgImgElement) {
        gsap.fromTo(bgImgElement, 
          { yPercent: 10 },
          { yPercent: -10, ease: "none",
            scrollTrigger: {
              trigger: sectionElement,
              start: "top bottom",
              end: "bottom top",
              scrub: true
            }
          }
        );
      }
      
    }, mainRef); // Scope the context to the main element
    
    // Cleanup function returned by gsap.context()
    return () => ctx.revert();
    
  }, [text, imagePosition, imageSrc, centered, backgroundImage]); // Add props used inside useEffect to dependency array
  
  // Function to render regular image (no changes needed here)
  const renderImage = () => {
    if (!imageSrc) return null;
    if (imgError) {
      return (
        <div className={styles.imagePlaceholder}>
          <p>Pickleball Image</p>
        </div>
      );
    }
    return (
      <Image 
        src={imageSrc} 
        alt="Pickleball illustration" 
        width={500}
        height={375}
        className={`${styles.sectionImage} ${styles.responsiveImage}`}
        onError={() => setImgError(true)}
        unoptimized={imageSrc.startsWith('http')} 
        priority={true}
      />
    );
  };
  
  return (
    // Add mainRef to the root element of the component
    <div 
      ref={mainRef} 
      className={`${styles.animatedSection} ${centered ? styles.centered : ''} ${imagePosition === 'left' ? styles.imageLeft : imagePosition === 'right' ? styles.imageRight : ''} ${backgroundImage ? styles.parallaxSection : ''}`}
      style={backgroundImage ? { position: 'relative' } : {}}
    >
      {/* Background Image (no ref needed) */}
      {backgroundImage && !bgImgError && (
        <Image
          // ref={bgImageRef} // Removed ref
          src={backgroundImage}
          alt="Background" // Used for querySelector
          fill={true}
          sizes="100vw"
          priority={true}
          style={{
            objectFit: 'cover',
            zIndex: 0,
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'scale(1.2)', // Keep scale adjustment
          }}
          onError={() => setBgImgError(true)}
          unoptimized={backgroundImage.startsWith('http')}
        />
      )}
      {backgroundImage && bgImgError && (
        <div className={styles.imagePlaceholder} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
          <p>Background Image Unavailable</p>
        </div>
       )}

      {/* Overlay (no changes needed) */}
      {backgroundImage && (
        <div className={styles.backgroundOverlay} style={{ zIndex: 1 }}></div>
      )}

      {/* Left Image Container (no ref needed) */}
      {imageSrc && imagePosition === 'left' && (
        <div className={styles.imageContainer} /* ref={leftImageRef} removed */ style={{ position: 'relative', zIndex: 2 }}>
          {renderImage()}
        </div>
      )}

      {/* Text container (no ref needed) */}
      <div className={styles.textContainer} style={{ position: 'relative', zIndex: 2 }}>
        {/* ref={textRef} removed */}
        <h2>
          {text}
          <span style={{ opacity: 0 }}>.</span>
        </h2>
      </div>

      {/* Right Image Container (no ref needed) */}
      {imageSrc && imagePosition === 'right' && (
        <div className={styles.imageContainer} /* ref={rightImageRef} removed */ style={{ position: 'relative', zIndex: 2 }}>
          {renderImage()}
        </div>
      )}
    </div>
  );
};

const About = () => {
  // No useEffect needed here anymore if only purpose was plugin registration
  // useEffect(() => {
  //   gsap.registerPlugin(ScrollTrigger);
  // }, []);
  
  return (
    <section className={styles.about} id="about">
      <div className={styles.aboutHeader}>
        <div className={styles.aboutCol}>
          <p className={`callout ${styles.aboutCallout}`}>Pickleball meets technology</p>
        </div>
      </div>
      
      <AnimatedSection 
        text="GameMax will revolutionize the future of pickleball through immersive virtual environments, intelligent training systems, and emotionally resonant design"
        imagePosition="left"
        imageSrc="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/436b839d-2bb6-4544-62ab-81c3e8cf4c00/public"
      />
      
      <AnimatedSection 
        text="Bridging sport, technology, and community into a unified Web3 experience that inspires growth, connection, and greatness on and off the court"
        imagePosition="right"
        imageSrc="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/6b7efcab-0849-4c3e-9ebc-9a5510652400/public"
      />
      
      <AnimatedSection 
        text="We're targeting 726M racket sports players worldwide, starting with 48M in pickleball"
        centered={true}
        backgroundImage="https://imagedelivery.net/LOBxX7gyPpyC22XVZKaoOw/671dcc04-ed23-4ab9-ce95-9e03acbe2200/public"
      />
    </section>
  );
};

export default About; 
